import clsx from 'clsx';
import { Fragment, useMemo, type ReactNode } from 'react';
import { useI18n } from '../lib/i18n';
import { STICKY_COLORS, type NoteImage, type StickyColor } from '../lib/notes';

/**
 * How a sticky note looks on My notes: its colour, its formatted words and its
 * pictures. Light on purpose — the board draws every note with it — while the
 * editor (NoteEditor.tsx, TipTap) loads only once someone writes.
 */

/** A sticky note's colours, from the design tokens: a light paper in the light theme, a tint in the dark one. */
export const NOTE_STYLE: Record<StickyColor, { card: string; swatch: string }> = {
  AMBER: {
    card: 'bg-signal-100 border-signal-300 dark:bg-signal-400/10 dark:border-signal-400/30',
    swatch: 'bg-signal-400',
  },
  CRIMSON: {
    card: 'bg-primary-50 border-primary-200 dark:bg-primary-400/10 dark:border-primary-400/30',
    swatch: 'bg-primary-500',
  },
  CYAN: {
    card: 'bg-info-bg border-info/30 dark:bg-ai-circuit/10 dark:border-ai-circuit/30',
    swatch: 'bg-ai-circuit',
  },
  GREEN: {
    card: 'bg-success-bg border-success/30 dark:bg-success/10 dark:border-success/30',
    swatch: 'bg-success',
  },
  VIOLET: {
    card: 'bg-ai-plasma/10 border-ai-plasma/25 dark:bg-ai-plasma/15 dark:border-ai-plasma/35',
    swatch: 'bg-ai-plasma',
  },
  GRAPHITE: {
    card: 'bg-neutral-100 border-neutral-300 dark:bg-neutral-500/15 dark:border-neutral-500/40',
    swatch: 'bg-neutral-500',
  },
};

/** The six colours, as round swatches: one is chosen. */
export function ColorChoice({ value, onChange }: { value: StickyColor; onChange: (color: StickyColor) => void }) {
  const { t } = useI18n();
  return (
    <div role="radiogroup" aria-label={t('notes.sticky.color')} className="flex items-center gap-1.5">
      {STICKY_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          role="radio"
          aria-checked={value === color}
          aria-label={t(`notes.color.${color}`)}
          title={t(`notes.color.${color}`)}
          onClick={() => onChange(color)}
          className={clsx(
            'h-6 w-6 rounded-full border-2 transition-transform duration-fast',
            NOTE_STYLE[color].swatch,
            value === color ? 'scale-110 border-[var(--text-primary)]' : 'border-transparent hover:scale-110',
          )}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/** The marks a note keeps, whatever another editor called them. */
const MARKS: Record<string, 'strong' | 'em' | 'u' | 's'> = {
  STRONG: 'strong',
  B: 'strong',
  EM: 'em',
  I: 'em',
  U: 'u',
  S: 's',
  DEL: 's',
  STRIKE: 's',
};

/**
 * One node of a note's rich text as React draws it: the blocks and marks the
 * note keeps, and only the words of anything else. Nothing of the API's HTML
 * is put into the page as HTML — no attribute but a block's direction, no
 * element the note does not keep — on top of the API's own cleaning.
 */
function draw(node: ChildNode, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const element = node as Element;
  const inner = () => Array.from(element.childNodes).map((child, i) => draw(child, `${key}.${i}`));
  const mark = MARKS[element.tagName];
  if (mark) {
    const Mark = mark;
    return <Mark key={key}>{inner()}</Mark>;
  }
  switch (element.tagName) {
    case 'P':
      // An empty paragraph is a blank line, as in the editor.
      return (
        <p key={key} dir="auto">
          {element.textContent ? inner() : <br />}
        </p>
      );
    case 'UL':
    case 'OL':
      return (
        <ul key={key} dir="auto">
          {Array.from(element.children)
            .filter((child) => child.tagName === 'LI')
            .map((child, i) => draw(child, `${key}.${i}`))}
        </ul>
      );
    case 'LI':
      return (
        <li key={key} dir="auto">
          {inner()}
        </li>
      );
    case 'BR':
      return <br key={key} />;
    default:
      return <Fragment key={key}>{inner()}</Fragment>;
  }
}

/**
 * A note's words, formatted as they were written: bold, italic, underline,
 * strikethrough and bullets, each paragraph a line, reading in its own
 * direction. The browser reads the HTML without running or loading anything
 * in it — a parsed document has no window — and only what `draw` keeps is drawn.
 */
export function NoteText({ html, className, onDoubleClick }: { html: string; className?: string; onDoubleClick?: () => void }) {
  const nodes = useMemo(() => {
    if (!html) return null;
    const body = new DOMParser().parseFromString(html, 'text/html').body;
    return Array.from(body.childNodes).map((node, i) => draw(node, String(i)));
  }, [html]);
  if (!nodes) return null;
  return (
    <div className={clsx('note-text', className)} onDoubleClick={onDoubleClick}>
      {nodes}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pictures
// ---------------------------------------------------------------------------

/**
 * A note's pictures, above its words as Microsoft Sticky Notes puts them: one
 * across the note, more in a grid. Each opens full size in a tab of its own, as
 * a picture on a task does.
 */
export function NoteImages({ images, className }: { images: NoteImage[]; className?: string }) {
  const { t } = useI18n();
  if (images.length === 0) return null;
  const one = images.length === 1;
  return (
    <ul className={clsx('grid gap-1.5', one ? 'grid-cols-1' : images.length === 2 || images.length === 4 ? 'grid-cols-2' : 'grid-cols-3', className)}>
      {images.map((image) => (
        <li key={image.id} className="min-w-0">
          <a
            href={image.url}
            target="_blank"
            rel="noopener noreferrer"
            title={image.originalName}
            aria-label={t('notes.sticky.openImage', { name: image.originalName })}
            className="block overflow-hidden rounded-md border border-black/10 bg-[var(--surface-sunken)] dark:border-white/10"
          >
            <img
              src={image.url}
              alt={image.originalName}
              loading="lazy"
              className={clsx('w-full object-cover transition-transform duration-fast hover:scale-[1.02]', one ? 'max-h-56' : 'aspect-square')}
            />
          </a>
        </li>
      ))}
    </ul>
  );
}
